import React, { useEffect, useRef, useState } from "react";
import {
  Play,
  Pause,
  Volume2,
  VolumeX,
  Download,
  X,
  Rewind,
  FastForward,
} from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { useLanguage } from "@/i18n/LanguageContext";

function formatTime(sec) {
  if (!isFinite(sec) || sec < 0) return "0:00";
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export default function AudioPlayer({ src, meta, onClose }) {
  const { t } = useLanguage();
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.9);
  const [muted, setMuted] = useState(false);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    a.volume = volume;
    a.muted = muted;
  }, [volume, muted]);

  useEffect(() => {
    // auto-play when a new src is set
    const a = audioRef.current;
    if (!a) return;
    a.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
  }, [src]);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      a.play();
      setPlaying(true);
    } else {
      a.pause();
      setPlaying(false);
    }
  };

  const seek = (v) => {
    const a = audioRef.current;
    if (!a || !isFinite(duration)) return;
    a.currentTime = v[0];
    setCurrent(v[0]);
  };

  const skip = (delta) => {
    const a = audioRef.current;
    if (!a) return;
    a.currentTime = Math.max(0, Math.min(duration, a.currentTime + delta));
  };

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = src;
    link.download = meta?.filename || "audioflip.mp3";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div
      data-testid="audio-player"
      className="fixed bottom-0 left-0 right-0 z-50 border-t border-zinc-200/70 bg-white/80 backdrop-blur-xl backdrop-saturate-150 shadow-[0_-8px_30px_-12px_rgba(0,47,167,0.15)]"
    >
      <audio
        ref={audioRef}
        src={src}
        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration || 0)}
        onTimeUpdate={(e) => setCurrent(e.currentTarget.currentTime || 0)}
        onEnded={() => setPlaying(false)}
      />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 py-3 sm:py-4">
        <div className="flex items-center gap-3 sm:gap-6">
          {/* Skip back */}
          <button
            data-testid="player-skip-back"
            onClick={() => skip(-10)}
            className="hidden sm:inline-flex h-9 w-9 items-center justify-center rounded-full text-zinc-700 hover:text-[#002FA7] hover:bg-zinc-100 transition-colors duration-200"
            aria-label={t("player.rewind")}
          >
            <Rewind className="h-4 w-4" />
          </button>

          {/* Play/Pause */}
          <button
            data-testid="player-play-pause"
            onClick={toggle}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#002FA7] text-white hover:bg-[#00258A] transition-colors duration-200"
            aria-label={playing ? t("player.pause") : t("player.play")}
          >
            {playing ? (
              <Pause className="h-5 w-5" fill="currentColor" />
            ) : (
              <Play className="h-5 w-5 pl-0.5" fill="currentColor" />
            )}
          </button>

          {/* Skip forward */}
          <button
            data-testid="player-skip-forward"
            onClick={() => skip(10)}
            className="hidden sm:inline-flex h-9 w-9 items-center justify-center rounded-full text-zinc-700 hover:text-[#002FA7] hover:bg-zinc-100 transition-colors duration-200"
            aria-label={t("player.forward")}
          >
            <FastForward className="h-4 w-4" />
          </button>

          {/* Meta + progress */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1.5">
              <div className="min-w-0 flex items-center gap-2">
                <span
                  data-testid="player-filename"
                  className="truncate font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-800 font-semibold"
                >
                  {meta?.filename || "audioflip.mp3"}
                </span>
                <span className="hidden sm:inline font-mono text-[10px] uppercase tracking-widest text-zinc-400">
                  · {meta?.voice} · {meta?.speed?.toFixed?.(2)}x
                </span>
              </div>
              <span
                data-testid="player-time"
                className="font-mono text-[11px] tabular-nums text-zinc-500"
              >
                {formatTime(current)} / {formatTime(duration)}
              </span>
            </div>
            <Slider
              data-testid="player-progress"
              value={[current]}
              min={0}
              max={duration || 0.0001}
              step={0.01}
              onValueChange={seek}
              className="[&_.bg-primary]:bg-[#002FA7] [&_.border-primary\\/50]:border-[#002FA7] [&_.bg-primary\\/20]:bg-zinc-200"
            />
          </div>

          {/* Volume */}
          <div className="hidden md:flex items-center gap-2 w-32">
            <button
              data-testid="player-mute"
              onClick={() => setMuted(!muted)}
              className="text-zinc-700 hover:text-[#002FA7] transition-colors duration-200"
              aria-label={t("player.mute")}
            >
              {muted || volume === 0 ? (
                <VolumeX className="h-4 w-4" />
              ) : (
                <Volume2 className="h-4 w-4" />
              )}
            </button>
            <Slider
              data-testid="player-volume"
              value={[muted ? 0 : volume]}
              min={0}
              max={1}
              step={0.01}
              onValueChange={(v) => {
                setVolume(v[0]);
                setMuted(false);
              }}
              className="[&_.bg-primary]:bg-zinc-800 [&_.border-primary\\/50]:border-zinc-800 [&_.bg-primary\\/20]:bg-zinc-200"
            />
          </div>

          {/* Download */}
          <button
            data-testid="player-download"
            onClick={handleDownload}
            className="inline-flex items-center gap-1.5 rounded-md border border-[#002FA7] bg-white px-3 py-2 text-xs sm:text-sm font-semibold text-[#002FA7] hover:bg-[#EFF4FF] transition-colors duration-200"
          >
            <Download className="h-4 w-4" />
            <span className="hidden sm:inline">{t("player.download")}</span>
          </button>

          {/* Close */}
          <button
            data-testid="player-close"
            onClick={onClose}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 transition-colors duration-200"
            aria-label={t("player.close")}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
